import React from 'react';
import classnames from 'classnames';
import { ArrowIcon, DeleteIcon, Pencil } from '../../images';
import styles from './Content.module.scss';
import { ContentBox } from './ContentBox.component';
import update from 'immutability-helper';
import { Input } from 'antd';
import { useDrop } from 'react-dnd';
import { UpdatedIndication } from '../updatedIndication';
import { Tooltip } from '../core';
import { Lang } from 'shared/constants';
import { CreateBox } from '../admin';
const { TextArea } = Input;

const ContentItemComponent = React.memo(function ContentItemComponent({
  id,
  title,
  subtitle,
  children,
  knowledgeItems,
  itemsLastUpdatedAt,
  adminMode,
  activeId,
  knowledgeSelectItem,
  deleteCategory,
  editCategory,
  createCategory,
  fullPath,
  path,
  type,
  updateCategory,
  updateCategorySubcategories,
  lastItem,
  firstItem,
  changeSectionOrder,
  ownerId,
  index,
  tags,
  options,
  lang,
  labels,
  ...item
}) {
  // drag and drop items
  const dndGroup = `contentItems-${id}`;
  const [, drop] = useDrop({ accept: dndGroup });
  const [items, setItems] = React.useState(children);
  let isLoading = false;

  React.useEffect(() => {
    setItems(children);
  }, [children]);

  const dragItem = (id, atIndex) => {
    const { item, index } = findItem(id);
    setItems(
      update(items, {
        $splice: [
          [index, 1],
          [atIndex, 0, item],
        ],
      }),
    );
  };

  const findItem = id => {
    const item = items.find(i => `${i.id}` === id);
    return {
      item,
      index: items.indexOf(item),
    };
  };

  const finishDrag = categoryId => {
    updateCategorySubcategories({
      id,
      categoryId,
      index: findItem(categoryId).index,
    });
  };

  // Modification actions
  const deleteCategoryAction = React.useCallback(() => {
    // TODO: Analytics
    deleteCategory({ id, title });
  }, [deleteCategory, id, title]);

  // Modification actions
  const editCategoryAction = React.useCallback(() => {
    // TODO: Analytics
    editCategory({ id, title, subtitle, ownerId, path, type, tags, options, labels });
  }, [editCategory, id, title, subtitle, ownerId, path, type, tags, options, labels]);

  const createNewCategoryAction = React.useCallback(() => {
    // TODO: Analytics
    createCategory({ path: fullPath, ownerId, tags, labels });
  }, [createCategory, fullPath, ownerId, tags, labels]);

  const changeOrder = React.useCallback(
    async ({ target }) => {
      if (isLoading) return false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isLoading = true;

      let { dataset } = target;

      let toIndex = dataset.direction === 'up' ? index - 1 : index + 1;
      await changeSectionOrder(id, toIndex);
      isLoading = false;
    },
    [index, id],
  );

  const renderItems = () => {
    if (!items) return null;

    return items.map(item => (
      <ContentBox
        key={`content-box-${item.id}`}
        {...item}
        {...{
          adminMode,
          activeId,
          knowledgeSelectItem,
          deleteCategory,
          updateCategory,
          editCategory,
          dragItem,
          findItem,
          dndGroup,
          finishDrag,
          lang,
        }}
      />
    ));
  };

  const renderAdminActions = () => {
    if (!adminMode) return null;

    return (
      <div className={styles.adminActions}>
        {!firstItem && (
          <Tooltip title={lang.MOVE_UP}>
            <ArrowIcon
              data-direction="up"
              onClick={changeOrder}
              className={classnames(styles.changeOrderIcon, styles.changeOrderUp)}
            />
          </Tooltip>
        )}
        {!lastItem && (
          <Tooltip title={lang.MOVE_DOWN}>
            <ArrowIcon
              data-direction="down"
              onClick={changeOrder}
              className={classnames(styles.changeOrderIcon, styles.changeOrderDown)}
            />
          </Tooltip>
        )}
        <Tooltip title={lang.DELETE}>
          <DeleteIcon className={styles.deleteIcon} onClick={deleteCategoryAction} />
        </Tooltip>
        <Tooltip title={lang.EDIT}>
          <Pencil className={styles.editIcon} onClick={editCategoryAction} />
        </Tooltip>
      </div>
    );
  };

  const onHeaderPress = React.useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // TODO: add analytics

      e.target.blur();
    }
  }, []);

  const onBlur = React.useCallback(
    e => {
      let { value } = e.target;
      // TODO: add analytics

      if (value === title) return false;

      updateCategory({ id, title: value, tags });
    },
    [id, tags, title, updateCategory],
  );

  return (
    <div key={id} className={classnames(styles.item, adminMode ? styles.admin : {})}>
      <span className={styles.anchor} id={id} />
      <div className={styles.header}>
        <UpdatedIndication lastUpdate={itemsLastUpdatedAt} />
        <TextArea
          onKeyPress={onHeaderPress}
          onBlur={onBlur}
          className={styles.headerText}
          autoSize={{ maxRows: 1 }}
          disabled={!adminMode}
          defaultValue={title}
        />
        {renderAdminActions()}
      </div>
      <div ref={drop} className={styles.items}>
        {renderItems()}

        <CreateBox adminMode={adminMode} onClick={createNewCategoryAction} lang={lang} />
      </div>
    </div>
  );
});

ContentItemComponent.defaultProps = {
  itemsLastUpdatedAt: null,
  knowledgeItems: [],
  children: [],
  adminMode: false,
  activeId: null,
  fullPath: null,
  knowledgeSelectItem: () => {},
  createCategory: () => {},
  deleteCategory: () => {},
  editCategory: () => {},
  updateCategory: () => {},
  updateCategorySubcategories: () => {},
  changeSectionOrder: () => {},
  index: null,
  ownerId: null,
  lastItem: false,
  firstItem: false,
  lang: Lang.ACTION_BUTTON_TOOLTIPS,
};

export { ContentItemComponent };
